
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  File, 
  FileText, 
  Loader2,
  Pencil, 
  Share2, 
  Trash2,
  Download,
  Building2,
  User
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { DocumentDetailHeader } from '@/components/documents/DocumentDetailHeader';
import { DocumentPreview } from '@/components/documents/DocumentPreview';
import { DocumentShareDialog } from '@/components/documents/DocumentShareDialog';
import { DocumentStats } from '@/components/documents/DocumentStats';
import { DocumentVersionsList } from '@/components/documents/DocumentVersionsList';
import { DeleteConfirmation } from '@/components/ui/delete-confirmation';
import { 
  fetchDocumentById, 
  fetchDocumentVersions, 
  getDownloadUrl, 
  fetchDocumentPermissions,
  deleteDocument,
  type DocumentVersionInfo
} from '@/services/document/documentService';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export default function DocumentDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [activeTab, setActiveTab] = useState('preview');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  const { data: document, isLoading: isLoadingDocument, error: documentError } = useQuery({
    queryKey: ['document', id],
    queryFn: () => fetchDocumentById(id!),
    enabled: !!id
  });

  const { data: versions, isLoading: isLoadingVersions } = useQuery({
    queryKey: ['document-versions', id],
    queryFn: () => fetchDocumentVersions(id!),
    enabled: !!id
  });

  const { data: permissions, isLoading: isLoadingPermissions } = useQuery({
    queryKey: ['document-permissions', id],
    queryFn: () => fetchDocumentPermissions(id!),
    enabled: !!id
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      toast({
        title: "Document deleted",
        description: "The document has been successfully deleted."
      });
      navigate('/documents');
    },
    onError: (error) => {
      toast({
        title: "Error deleting document",
        description: "There was an error deleting the document. Please try again.",
        variant: "destructive"
      });
    }
  });

  // Find current version from versions list
  const currentVersion = versions?.find(v => v.id === selectedVersion) || versions?.[0];

  const handleVersionSelect = (versionId: string) => {
    setSelectedVersion(versionId);
  };

  const handleDeleteDocument = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const downloadDocument = async () => {
    if (!document) return;
    
    try {
      const url = await getDownloadUrl(document.file_path);
      
      // Create a hidden link and simulate a click to download - using window.document instead of document
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.title;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      toast({
        title: "Download started",
        description: `Downloading ${document.title}`
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to generate download link",
        variant: "destructive"
      });
    }
  };

  if (isLoadingDocument) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (documentError || !document) {
    return (
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle>Document Not Found</CardTitle>
          <CardDescription>
            The document you're looking for doesn't exist or you don't have permission to view it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="secondary" asChild>
            <Link to="/documents">Return to Documents</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with document info and actions */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/documents">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{document.title}</h1>
            {document.description && (
              <p className="text-muted-foreground mt-1">{document.description}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={downloadDocument} className="gap-2">
            <Download className="h-4 w-4" />
            Download
          </Button>
          
          <Button variant="outline" asChild className="gap-2">
            <Link to={`/documents/${id}/edit`}>
              <Pencil className="h-4 w-4" />
              Edit
            </Link>
          </Button>
          
          <Button variant="outline" onClick={() => setShowShareDialog(true)} className="gap-2">
            <Share2 className="h-4 w-4" />
            Share
          </Button>
          
          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar with document info */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Document Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">File Type</p>
                <p className="text-sm">{document.file_type}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Size</p>
                <p className="text-sm">{(document.file_size / 1024).toFixed(2)} KB</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Uploaded By</p>
                <div className="flex items-center space-x-2 mt-1">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-xs">
                      {document.uploaded_by_user?.first_name?.[0]}{document.uploaded_by_user?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm">{document.uploaded_by_user?.first_name} {document.uploaded_by_user?.last_name}</span>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Shared With</p>
                {isLoadingPermissions ? (
                  <div className="flex items-center space-x-2 mt-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="text-sm">Loading...</span>
                  </div>
                ) : permissions && permissions.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {permissions.slice(0, 3).map((permission: any) => (
                      <div key={permission.id} className="flex items-center space-x-1">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-xs">
                            {permission.user?.first_name?.[0]}{permission.user?.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs">{permission.user?.first_name}</span>
                      </div>
                    ))}
                    {permissions.length > 3 && (
                      <Button variant="ghost" size="sm" className="h-5 px-2" onClick={() => setShowShareDialog(true)}>
                        +{permissions.length - 3} more
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">Not shared with anyone</p>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Related To</p>
                <div className="space-y-2 mt-1">
                  {document.organization && (
                    <div className="flex items-center space-x-2">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <Link to={`/organizations/${document.organization.id}`} className="text-sm hover:underline">
                        {document.organization.name}
                      </Link>
                    </div>
                  )}
                  
                  {document.contact && (
                    <div className="flex items-center space-x-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      <Link to={`/contacts/${document.contact.id}`} className="text-sm hover:underline">
                        {document.contact.first_name} {document.contact.last_name}
                      </Link>
                    </div>
                  )}

                  {!document.organization && !document.contact && (
                    <p className="text-sm text-muted-foreground">Not associated with any record</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

            <DocumentVersionsList 
            versions={versions || []} 
            isLoading={isLoadingVersions}
            selectedVersionId={selectedVersion}
            onVersionSelect={handleVersionSelect}
          />
        </div>

        {/* Main content */}
        <div className="lg:col-span-3">
          <Card className="h-full">
            <CardHeader className="border-b px-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-3 w-full sm:w-[400px]">
                  <TabsTrigger value="preview">Preview</TabsTrigger>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>

            <CardContent className="p-6">
              <TabsContent value="preview" className="pt-4">
                <DocumentPreview 
                  document={document}
                  onDownload={downloadDocument}
                  onShare={() => setShowShareDialog(true)}
                />
              </TabsContent>

              <TabsContent value="details" className="pt-0 lg:pt-4">
                <DocumentDetailHeader 
                  document={document} 
                  onShare={() => setShowShareDialog(true)}
                  onDownload={downloadDocument}
                  onDelete={() => setShowDeleteDialog(true)}
                  confirmDelete={handleDeleteDocument}
                />
                <Separator className="my-6" />
                <DocumentStats document={document} />
              </TabsContent>

              <TabsContent value="activity" className="pt-0 lg:pt-4">
                <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
                  <FileText className="h-10 w-10 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">Activity log coming soon</h3>
                  <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-md">
                    Track document views, downloads, and interactions in this upcoming feature.
                  </p>
                </div>
              </TabsContent>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Share Dialog */}
      <DocumentShareDialog 
        open={showShareDialog} 
        onOpenChange={setShowShareDialog} 
        documentId={id!} 
        currentPermissions={permissions || []}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteDocument}
        title="Delete Document"
        description={`Are you sure you want to delete "${document.title}"? This action cannot be undone and will remove all associated versions and permissions.`}
        entityName="document"
      />
    </div>
  );
}
